import {Localizer} from "../../../infrastructure/localization/localizer";
import React, {useCallback, useEffect, useState} from "react";
import {ForloebOverviewFilterModel} from "./ForloebOverviewFilterModel";
import { UddannelseTypeEnum } from "../../sharedmodels/stilling/uddannelseTypeEnum";
import {Loading} from "../loading/loading";
import {IForloebOverviewFilterProps} from "./forloebOverviewFilterProps";
import { DateFilterInput, getDateFilterInput } from "./ForloebOverviewDateFilterModel";
import { DateFilter } from "./filterComponents/DateFilter";
import { Dropdown, DropdownOption } from "../dropdown/dropdown";
import useEnumDropdownOptions from "../dropdown/hooks/useEnumDropdownOptions";
import { nameof } from "ts-simple-nameof";
import {ActionMeta, SingleValue} from "react-select";
import {GetKbuRundeEnumTranslation, KbuRundeEnum} from "../../sharedmodels/user/kbuRundeEnum";
import useStamdataApi from "../../hooks/useStamdataApi";
import EducationFilter, { EducationFilterResult } from "../filter/components/educationFilter";
import PlaceFilter, { PlaceFilterResult } from "../filter/components/placeFilter";
import useUserFilterPermissions from "core/hooks/useUserFilterPermissions";
import useUddannelseModels from "../dropdown/hooks/useUddannelseModels";
import PlaceAndEducationCrudFilterRestrictModel, { ToRequestModelFromForloebOverviewFilterModel } from "core/sharedmodels/restrictModel/PlaceAndEducationCrudFilterRestrictModel";
import { hasAnyDefinedProperties } from "index";
import { useAppSelector } from "app/hooks";
import { ForloebOverviewFiltersState, forloebOverviewFiltersReducer } from "./forloebOverviewFiltersSlice";

const hasFiltersToRestrict = (filterModel: ForloebOverviewFilterModel) => {
    return hasAnyDefinedProperties({
        specialer: filterModel.specialer,
        uddannelser: filterModel.uddannelser,
        regioner: filterModel.regioner,
        omraader: filterModel.omraader,
        afdelinger: filterModel.afdelinger,
        praksiser: filterModel.praksis,
        sygehuse: filterModel.hospitaler,
    });
}

export const CrudFilterInputModel = (props: IForloebOverviewFilterProps) => {
    const { permissions, isLoadingPermissions } = useUserFilterPermissions();
    const { stamdataKbuRundeApi, stamdataRestrictApi } = useStamdataApi();
    const { toIdsFromUddannelsesTyper, toUddannelsesTyperFromId, isLoading: isFetching } = useUddannelseModels();

    const [filterModel, setFilterModel] = useState<ForloebOverviewFilterModel>(props.filter);
    const [isLoading, setIsLoading] = useState(true);
    const [kbuPeriodOptions] = useEnumDropdownOptions<KbuRundeEnum>(KbuRundeEnum,GetKbuRundeEnumTranslation)
    const [kbuRundesSpan, setKbuRundesSpan] = useState<DropdownOption<string>[]>([]);
    const [showKbuInputFields, setShowKbuInputFields] = useState<boolean | undefined>(false);

    const [filterRestrictModel, setFilterRestrictModel] = useState<PlaceAndEducationCrudFilterRestrictModel>();

    const forloebOverviewFilterSliceState = useAppSelector(forloebOverviewFiltersReducer) as ForloebOverviewFiltersState;

    const propsOnChangeCallback = props.onChangeCallback;

    useEffect(() => {
        if (propsOnChangeCallback) {
            propsOnChangeCallback(filterModel);
        }
    }, [filterModel, propsOnChangeCallback]);

    useEffect(() => {
        const fetchFilterRestrictModel = async () => {
            const hasFilterToRestrict = hasFiltersToRestrict(filterModel);
            setFilterRestrictModel(hasFilterToRestrict
                ? await stamdataRestrictApi.getPlaceAndEducationRestrictions(ToRequestModelFromForloebOverviewFilterModel(filterModel))
                : undefined);
        }
        fetchFilterRestrictModel();
    }, [filterModel, stamdataRestrictApi])

    const onEducationFilterChange = useCallback((result: EducationFilterResult) => {
        setFilterModel(prev => (
            {
                ...prev,
                specialer: result.specialer,
                uddannelsetyper: toUddannelsesTyperFromId(result.uddannelser),
            }
        ));
    }, [toUddannelsesTyperFromId]);

    const onPlaceFilterChange = useCallback((result: PlaceFilterResult) => {
        setFilterModel(prev => ({
            ...prev, 
            regioner: result.regioner,
            omraader: result.omraader,
            afdelinger: result.afdelinger,
            praksis: result.praksiser,
            hospitaler: result.sygehuse,
            alleRegionerValgt: result.allRegionsSelected
        }));
    }, []);

    useEffect(() => {
        const getUddannelsesTypeAndKbuPeriod = async () => {
            const kbuRundesSpan = await stamdataKbuRundeApi.getKbuPeriodSpan();
            setKbuRundesSpan(kbuRundesSpan.map(sub => ({label: sub.toString(), value: sub.toString()})))
            setIsLoading(false);
        }
        getUddannelsesTypeAndKbuPeriod();
    }, [stamdataKbuRundeApi]);

    useEffect(()=> {
        const showKbuFields = filterModel?.uddannelsetyper?.includes(UddannelseTypeEnum.KBU);

        setShowKbuInputFields(showKbuFields)
        if(!showKbuFields) {
            setFilterModel(prev => ({
                ...prev,
                kbuPeriod: undefined,
                kbuPeriodYear: undefined
            }));
        }
    },[filterModel?.uddannelsetyper])

    const handleKbuPeriodSelected = (newValue: SingleValue<DropdownOption<KbuRundeEnum>>, actionMeta: ActionMeta<DropdownOption<KbuRundeEnum>>) => {
        setFilterModel(prev => ({...prev,
            kbuPeriod: String(newValue?.value!!),
        }));
    }

    const handleKbuPeriodSpanSelected = (newValue:  SingleValue<DropdownOption<string>>) => {
        setFilterModel((prev) => ({...prev,
            kbuPeriodYear: newValue?.value,
        }));
    }

    const onDateFilterInputChanged = useCallback((forloebOverviewDateFilterModel: DateFilterInput) => {
        setFilterModel((prev) => ({...prev,
            dateFilterType: forloebOverviewDateFilterModel.dateFilterType,
            year: forloebOverviewDateFilterModel.year,
            date: forloebOverviewDateFilterModel.date.toIsoDateTimeString(),
            periodFrom: forloebOverviewDateFilterModel.periodFrom.toIsoDateTimeUtcString(),
            periodTo: forloebOverviewDateFilterModel.periodTo.toIsoDateTimeUtcString()
        }));
    }, []);

    return (
    <>
        <DateFilter
            modalId={props.modalId}
            filter={getDateFilterInput(filterModel)}
            callback={onDateFilterInputChanged}
        />

        <Loading isLoading={isLoading || isFetching || isLoadingPermissions} text={Localizer.global_loadingFilters()} spinnerClasses={isLoading ? "padding-top-l" : ""}>

            <EducationFilter 
                onChange={onEducationFilterChange} 
                permissions={permissions} 
                titled
                additionalFilterTitleClasses={"padding-top-m"}
                initialSelectedValues={{
                    specialer: filterModel?.specialer ?? [],
                    uddannelser: toIdsFromUddannelsesTyper(filterModel?.uddannelsetyper),
                }}
                restrictModel={filterRestrictModel}
            />

            {showKbuInputFields &&
                <>
                    <label>{Localizer.global_kburunde()}</label>
                    <div className={"d-flex justify-content-between"}>
                        <div className="margin-bottom-m mr-3 w-50">
                            <Dropdown
                                name={nameof<ForloebOverviewFilterModel>(model => model.kbuPeriod)}
                                placeholder={Localizer.global_period()}
                                options={kbuPeriodOptions}
                                onChange={handleKbuPeriodSelected}
                                value={kbuPeriodOptions.find(x => x.value === Number(filterModel.kbuPeriod))}
                                closeMenuOnSelect={false}
                                isClearable
                            />
                        </div>

                        <div className="margin-bottom-m ml-3 w-50">
                            <Dropdown
                                name={nameof<ForloebOverviewFilterModel>(model => model.uddannelsetyper)}
                                placeholder={Localizer.global_aar()}
                                options={kbuRundesSpan}
                                onChange={(newVal) => handleKbuPeriodSpanSelected(newVal)}
                                closeMenuOnSelect={false}
                                value={kbuRundesSpan.find(x=> x.value === filterModel?.kbuPeriodYear)}
                                isClearable
                            />
                        </div>
                    </div>
                </>
            }

            <PlaceFilter 
                onChange={onPlaceFilterChange} 
                permissions={permissions}
                preselectViaPermissions={!forloebOverviewFilterSliceState.anyFiltersActive}
                titled
                additionalFilterTitleClasses={"padding-top-m"}
                initialSelectedValues={{
                    regioner: filterModel.regioner ?? [],
                    omraader: filterModel.omraader ?? [],
                    afdelinger: filterModel.afdelinger ?? [],
                    praksiser: filterModel.praksis ?? [],
                    sygehuse: filterModel.hospitaler ?? [],
                }}
                restrictModel={filterRestrictModel}
            />

            <div className="border-bottom">&nbsp;</div>
        </Loading>
    </>
    )
}
